import React, { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { comprimirImagen } from "@/lib/image";
import { INCIDENTE_TIPO_EN } from "@/lib/chofer";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";
// Fix Q (2026-10-13): «Report a problem» en inglés y dentro de la pantalla (abajo de cada pantalla del chofer), no flotando.
export default function ProblemaButton() {
  const [open, setOpen] = useState(false);
  const [activas, setActivas] = useState([]);
  const [tipo, setTipo] = useState("");
  const [cargaId, setCargaId] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [foto, setFoto] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!open) return;
    setTipo(""); setCargaId(""); setDescripcion(""); setFoto(null);
    api.get("/chofer/cargas-activas").then(({ data }) => {
      setActivas(data);
      if (data.length === 1) setCargaId(data[0].id);
    }).catch(() => {});
  }, [open]);
  const onFoto = async (e) => {
    const f = e.target.files?.[0];
    if (f) setFoto(await comprimirImagen(f));
    e.target.value = "";
  };
  const enviar = async () => {
    if (!tipo) return toast.error(t("Choose the type of problem"));
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("tipo", tipo);
      fd.append("descripcion", descripcion);
      if (cargaId) fd.append("carga_id", cargaId);
      if (foto) fd.append("foto", foto);
      await api.post("/chofer/incidentes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("Problem reported"));
      setOpen(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };
  return (
    <>
      <div className="max-w-md mx-auto px-4 pt-1 pb-4">
        <Button type="button" variant="outline" data-testid="problema-fab" onClick={() => setOpen(true)} className="w-full h-[52px] text-[16px] font-bold text-brand bg-white">{t("Report a problem")}</Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="text-ink">{t("Report a problem")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">{t("Type")}</Label>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(INCIDENTE_TIPO_EN).map(([k, v]) => (
                  <button
                    key={k}
                    data-testid={`problema-tipo-${k}`}
                    onClick={() => setTipo(k)}
                    className={cn("h-14 rounded-lg border-2 font-semibold transition-colors duration-150", tipo === k ? "border-danger bg-[#FBEDEC] text-danger" : "border-line-strong text-ink")}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
            {activas.length > 1 && (
              <div>
                <Label>{t("Related load")}</Label>
                <Select value={cargaId} onValueChange={setCargaId}>
                  <SelectTrigger data-testid="problema-carga-select" className="h-11 bg-white mt-1"><SelectValue placeholder={t("Choose a load")} /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {activas.map((c) => <SelectItem key={c.id} value={c.id}>{c.numero_contenedor}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {activas.length === 1 && <p className="text-xs text-ink-3">{t("It will be linked to load {numero_contenedor}.", { numero_contenedor: activas[0].numero_contenedor })}</p>}
            {activas.length === 0 && <p className="text-xs text-ink-3">{t("No active load: the problem will be reported without a load.")}</p>}
            <Textarea data-testid="problema-descripcion" placeholder={t("Description (optional)")} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={2} />
            <div>
              {foto ? (
                <div className="flex items-center justify-between bg-surface-2 rounded-lg px-3 py-2 text-sm">
                  <span className="truncate">{foto.name}</span>
                  <button onClick={() => setFoto(null)} className="text-ink-3" aria-label={t("Remove photo")}><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <label data-testid="problema-foto-btn" className="flex items-center justify-center gap-2 h-12 rounded-lg border-2 border-dashed border-line-strong text-ink-3 cursor-pointer">
                  <Camera className="h-5 w-5" />{t(" Photo (optional)")}<input type="file" accept="image/*" capture="environment" className="hidden" onChange={onFoto} />
                </label>
              )}
            </div>
            <Button data-testid="problema-enviar" onClick={enviar} disabled={sending || !tipo} variant="destructive" className="w-full h-12">
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : t("Send")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
